import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { IFluidMonitoring, IFluidMonitoringApiState } from '../types/fluidMonitoring.types';

const INITIAL_STATE: IFluidMonitoringApiState = {
    data: null,
    loading: 'idle',
    error: null,
    historyList: null,
    loadingHistory: 'idle',
};

export const getFluidMonitoringByAdmissionId = createAsyncThunk(
    'fluidMonitoring/getByAdmissionId',
    async (admissionId: number) => {
        const response = await axios.get(
            `/IPDAdmission/Fluid/GetByAdmissionId/${admissionId}`,
        );
        return response.data;
    },
);

export const getFluidMonitoringHistoryByAdmissionId = createAsyncThunk(
    'fluidMonitoring/getHistoryByAdmissionId',
    async (admissionId: number) => {
        const response = await axios.get(
            `/IPDAdmission/Fluid/GetByAdmissionId/${admissionId}`,
        );
        return response.data;
    },
);

export const saveFluidMonitoring = createAsyncThunk(
    'fluidMonitoring/save',
    async (data: IFluidMonitoring) => {
        const response = await axios.post('/IPDAdmission/Fluid/Save', data, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    },
);

export const updateFluidMonitoring = createAsyncThunk(
    'fluidMonitoring/update',
    async (data: IFluidMonitoring) => {
        const response = await axios.post('/IPDAdmission/Fluid/Update', data, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    },
);

export const fluidMonitoringSlice = createSlice({
    name: 'fluidMonitoring',
    initialState: INITIAL_STATE,
    reducers: {
        clearFluidMonitoringData: (state) => {
            state.data = null;
            state.error = null;
        },
        clearFluidMonitoringHistory: (state) => {
            state.historyList = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFluidMonitoringByAdmissionId.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getFluidMonitoringByAdmissionId.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                // API returns `result` as an array; store the first item (or null)
                state.data = Array.isArray(action.payload?.result)
                    ? action.payload.result[0] ?? null
                    : action.payload?.result ?? null;
                state.error = null;
            })
            .addCase(getFluidMonitoringByAdmissionId.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
                state.data = null;
            })
            .addCase(getFluidMonitoringHistoryByAdmissionId.pending, (state) => {
                state.loadingHistory = 'pending';
            })
            .addCase(getFluidMonitoringHistoryByAdmissionId.fulfilled, (state, action) => {
                state.loadingHistory = 'succeeded';
                const result = action.payload?.result;
                state.historyList = Array.isArray(result)
                    ? result
                    : result != null
                      ? [result]
                      : [];
            })
            .addCase(getFluidMonitoringHistoryByAdmissionId.rejected, (state) => {
                state.loadingHistory = 'failed';
                state.historyList = null;
            })
            .addCase(saveFluidMonitoring.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(saveFluidMonitoring.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = Array.isArray(action.payload?.result)
                    ? action.payload.result[0] ?? null
                    : action.payload?.result ?? null;
                state.error = null;
            })
            .addCase(saveFluidMonitoring.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(updateFluidMonitoring.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(updateFluidMonitoring.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = Array.isArray(action.payload?.result)
                    ? action.payload.result[0] ?? null
                    : action.payload?.result ?? null;
                state.error = null;
            })
            .addCase(updateFluidMonitoring.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            });
    },
});

export const { clearFluidMonitoringData, clearFluidMonitoringHistory } =
    fluidMonitoringSlice.actions;
export default fluidMonitoringSlice.reducer;
